import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom'
import { useAuthContext } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { CompanyProvider, useCompanyContext } from '../contexts/CompanyContext'
import { CompanySwitcher } from '../components/CompanySwitcher'
import { Avatar, ThemeToggle, ToastContainer, Loader } from '../components/ui'
import { getAvatarUrl } from '../utils/avatarUrl'
import { NotificationPanel } from '../components/notifications/NotificationPanel'
import { RoleSwitcher } from '../components/RoleSwitcher'
import { useCompanyFeatures } from '../hooks/useCompanyFeatures'
import { formatDocumentTitle } from '../utils/documentTitle'
import { 
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  ChevronDownIcon,
  MegaphoneIcon,
  HomeIcon,
  UsersIcon,
  ClockIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  FingerPrintIcon,
  FolderIcon,
  Squares2X2Icon,
  ShieldCheckIcon,
  BanknotesIcon,
  UserCircleIcon,
  KeyIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline'

// ... existing imports ...

// Page title and subtitle by route
const PAGE_TITLES: Record<string, { title: string; subtitle?: string }> = {
  '/dashboard': { title: 'Dashboard', subtitle: "Welcome back! Here's what's happening with your team today." },
  '/dashboard/employees': { title: 'Employees', subtitle: 'Manage your team members and their roles' },
  '/dashboard/attendance': { title: 'Attendance', subtitle: 'Track and manage employee attendance records' },
  '/dashboard/leave': { title: 'Leave', subtitle: 'Apply for leave and manage your applications' },
  '/dashboard/leave/config': { title: 'Leave Policy Configuration', subtitle: 'Configure leave entitlements and working days per year' },
  '/dashboard/departments': { title: 'Departments', subtitle: 'Manage organizational departments and structures' },
  '/dashboard/org-tree': { title: 'Org Tree', subtitle: 'Organizational hierarchy' },
  '/dashboard/holidays': { title: 'Holidays', subtitle: 'Manage and view company and public holidays' },
  '/dashboard/projects': { title: 'Projects', subtitle: 'Manage projects and team assignments' },
  '/dashboard/timesheet': { title: 'Timesheet Management', subtitle: 'Track and manage your work hours and project time' },
  '/dashboard/analytics': { title: 'Analytics', subtitle: 'View performance metrics and workforce analytics' },
  '/dashboard/reports': { title: 'Master Reports', subtitle: 'Generate and export reports' },
  '/dashboard/finance-operation': { title: 'Finance Operation', subtitle: 'Finance operations and workflows' },
  '/dashboard/settings': { title: 'Settings', subtitle: 'Configure system settings and user preferences' },
  '/dashboard/profile': { title: 'Profile', subtitle: 'Manage your account settings and preferences' },
  '/dashboard/admin/users': { title: 'User Management', subtitle: 'Manage users, roles, and permissions' },
  '/dashboard/admin/roles': { title: 'Role Management', subtitle: 'Create and manage roles and permissions' },
  '/dashboard/admin/companies': { title: 'Company Management', subtitle: 'Manage companies and organizations' },
  '/dashboard/announcements': { title: 'Announcements', subtitle: 'Create and manage company announcements' },
  '/dashboard/shifts': { title: 'Shift Management', subtitle: 'Create and manage work schedules and shift assignments' },
  '/dashboard/biometric': { title: 'Biometric', subtitle: 'Manage biometric devices and attendance logs (HR only)' },
}

// Icon mapping for navigation items
const iconMap = {
  HomeIcon,
  UsersIcon,
  ClockIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  BellIcon,
  MegaphoneIcon,
  FingerPrintIcon,
  FolderIcon,
  Squares2X2Icon,
  ShieldCheckIcon,
  BanknotesIcon,
}

const DashboardLayoutInner: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { 
    currentUser: user, 
    logout, 
    isLoading,
    getVisibleNavItems, 
    currentRole, 
    isAuthenticated 
  } = useAuthContext()
  const { selectedCompany, multiCompanyEnabled } = useCompanyContext()
  const { applicationName } = useCompanyFeatures(!!isAuthenticated)
  const { 
    state: { toasts, notifications, notificationPanelOpen },
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    closeNotificationPanel,
    toggleNotificationPanel,
    removeToast
  } = useNotifications()

  const currentPath = location.pathname
  const pageTitleSegment = useMemo(() => {
    let match: { title: string; subtitle?: string } | null = PAGE_TITLES[currentPath] ?? null
    if (!match) {
      const prefix = Object.keys(PAGE_TITLES)
        .filter((p) => currentPath.startsWith(p))
        .sort((a, b) => b.length - a.length)[0]
      match = prefix ? PAGE_TITLES[prefix] : null
    }
    return match?.title ?? null
  }, [currentPath])

  useEffect(() => {
    document.title = formatDocumentTitle(pageTitleSegment ?? undefined, applicationName)
  }, [pageTitleSegment, applicationName])

  const displayCompany = multiCompanyEnabled ? selectedCompany : user?.company
  const [companyLogoFailed, setCompanyLogoFailed] = useState(false)
  const [staticLogoFailed, setStaticLogoFailed] = useState(false)
  const lightLogoSrc = `${import.meta.env.BASE_URL}logo-light.png`
  const darkLogoSrc = `${import.meta.env.BASE_URL}logo.png`
  const companyLogoUrl = useMemo(() => {
    const raw = displayCompany?.logoUrl
    if (!raw || companyLogoFailed) return null
    const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace('/api', '')
    return `${apiBase}${raw.startsWith('/') ? '' : '/'}${raw}`
  }, [displayCompany?.logoUrl, companyLogoFailed])
  
  // Use useMemo to recalculate navigation when user, role, or authentication state changes
  const navigation = useMemo(() => {
    if (isAuthenticated && !isLoading && user) {
      const navItems = getVisibleNavItems()
      return navItems
    }
    return []
  }, [isAuthenticated, isLoading, user, currentRole]) // Removed getVisibleNavItems from dependencies
  
  const unreadCount = notifications.filter(n => !n.read).length

  const handleLogout = async () => {
    try {
      setUserMenuOpen(false)
      await logout()
      // After logout, redirect to login page
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('Logout failed:', error)
      // Still redirect to login page even if logout fails
      navigate('/login', { replace: true })
    }
  }

  // Close user menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (userMenuOpen && !target.closest('.user-menu-container')) {
        setUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userMenuOpen])

  // Reset logo fallbacks when company context changes
  useEffect(() => {
    setCompanyLogoFailed(false)
    setStaticLogoFailed(false)
  }, [displayCompany?.id, displayCompany?.logoUrl])



  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setSidebarOpen(false)
      setUserMenuOpen(false)
      closeNotificationPanel()
    }
  }

  // Show loading state while authentication is being initialized
  if (isLoading || !user || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-navy-900">
        <Loader
          variant="spinner"
          size="lg"
          label={isLoading ? 'Loading...' : 'Initializing...'}
        />
      </div>
    )
  }

  return (
    <div className="h-screen overflow-hidden bg-neutral-50 dark:bg-navy-900" onKeyDown={handleKeyDown}>
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        Skip to main content
      </a>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-navy-900/95 dark:backdrop-blur-xl shadow-lg dark:shadow-glass-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        aria-label="Main navigation"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <header className="flex items-center justify-between h-16 px-6 border-b border-neutral-200 dark:border-navy-700/50">
            <Link to="/dashboard" className="flex items-center space-x-2" aria-label={`${applicationName} Dashboard`}>
              {companyLogoUrl ? (
                <img
                  src={companyLogoUrl}
                  alt={displayCompany?.name || 'Company'}
                  className="h-8 w-auto object-contain"
                  onError={() => setCompanyLogoFailed(true)}
                />
              ) : !staticLogoFailed ? (
                <>
                  <img
                    src={lightLogoSrc}
                    alt="Edify"
                    className="h-8 w-8 object-contain dark:hidden"
                    onError={() => setStaticLogoFailed(true)}
                  />
                  <img
                    src={darkLogoSrc}
                    alt="Edify"
                    className="h-8 w-8 object-contain hidden dark:block"
                    onError={() => setStaticLogoFailed(true)}
                  />
                </>
              ) : null}
              <span className="text-xl font-bold text-neutral-900 dark:text-white">
                {displayCompany?.name || applicationName}
              </span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md text-neutral-400 hover:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="Close navigation menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </header>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 overflow-y-auto" role="navigation" aria-label="Main navigation">
            {navigation.length > 0 ? (
              <div key={`nav-${currentRole}-${user?.id}-${user?.email}`}>
                {navigation.map((item, idx) => {
                  const isActive = currentPath === item.href
                  const IconComponent = iconMap[item.icon as keyof typeof iconMap]
                  const prevGroup = idx > 0 ? (navigation[idx - 1] as typeof item).group : null
                  const showSeparator = idx > 0 && 'group' in item && item.group !== prevGroup

                  return (
                    <React.Fragment key={item.name}>
                      {showSeparator && (
                        <div className="my-2 border-t border-neutral-200 dark:border-navy-700/50" />
                      )}
                      <Link
                        to={item.href}
                        className={`
                          flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                          ${isActive 
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border-r-2 border-primary-600' 
                            : 'text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-navy-700/50 hover:text-neutral-900 dark:hover:text-white'
                          }
                        `}
                        onClick={() => setSidebarOpen(false)}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        <IconComponent className={`mr-3 h-5 w-5 ${isActive ? 'text-primary-600' : 'text-neutral-400'}`} aria-hidden="true" />
                        {item.name}
                      </Link>
                    </React.Fragment>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">No navigation items available</p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Check your permissions</p>
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-navy-700/50">
             <div className="flex items-center justify-center space-x-2 text-xs text-neutral-500 dark:text-neutral-400">
                <span>Powered by</span>
                <div className="flex items-center font-bold text-primary-600">
                  <img src={lightLogoSrc} alt="" className="w-4 h-4 object-contain mr-1 dark:hidden" />
                  <img src={darkLogoSrc} alt="" className="w-4 h-4 object-contain mr-1 hidden dark:block" />
                  {applicationName}
                </div>
             </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col h-screen min-w-0">
        {/* Topbar */}
        <header className="flex-shrink-0 z-30 bg-white dark:bg-navy-900/90 dark:backdrop-blur-xl border-b border-neutral-200 dark:border-navy-700/50">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-navy-800/50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              aria-label="Open navigation menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            {/* Page title - exact match or longest prefix for nested routes */}
            <div className="flex-1 flex items-center min-w-0 px-4">
              {(() => {
                let match: { title: string; subtitle?: string } | null = PAGE_TITLES[currentPath] ?? null
                if (!match) {
                  const prefix = Object.keys(PAGE_TITLES)
                    .filter(p => currentPath.startsWith(p))
                    .sort((a, b) => b.length - a.length)[0]
                  match = prefix ? PAGE_TITLES[prefix] : null
                }
                if (match) {
                  return (
                    <div className="min-w-0">
                      <h1 className="text-lg font-semibold text-neutral-900 dark:text-white truncate">{match.title}</h1>
                      {match.subtitle && (
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{match.subtitle}</p>
                      )}
                    </div>
                  )
                }
                return null
              })()}
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Company Switcher (when multi-company enabled) */}
              <CompanySwitcher />

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Notifications */}
              <button 
                onClick={toggleNotificationPanel}
                className="relative p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-navy-800/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
                aria-expanded={notificationPanelOpen}
                aria-haspopup="true"
              >
                <BellIcon className="h-6 w-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 h-2 w-2 bg-danger-500 rounded-full" aria-hidden="true"></span>
                )}
              </button>

              {/* User menu */}
              <div className="relative user-menu-container">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-neutral-100 dark:hover:bg-navy-800/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2"
                  aria-label={`User menu for ${user?.name}`}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <div className="relative">
                    <Avatar 
                      src={getAvatarUrl(user?.avatar)} 
                      name={user?.name} 
                      size="sm" 
                    />
                    <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-success-500 ring-2 ring-white dark:ring-navy-900" />
                  </div>
                  <ChevronDownIcon className={`h-3.5 w-3.5 text-neutral-400 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>

                {/* Dropdown menu */}
                {userMenuOpen && (
                  <div 
                    className="absolute right-0 mt-2 w-64 bg-white dark:bg-navy-800/95 dark:backdrop-blur-xl rounded-2xl shadow-xl dark:shadow-glass-lg border border-neutral-200/80 dark:border-navy-700/50 overflow-hidden z-50 animate-in fade-in slide-in-from-top-1 duration-150"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu-button"
                  >
                    {/* User info header */}
                    <div className="px-4 py-4 bg-gradient-to-br from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-navy-800/50">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Avatar 
                            src={getAvatarUrl(user?.avatar)} 
                            name={user?.name} 
                            size="md" 
                          />
                          <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-success-500 ring-2 ring-white dark:ring-navy-800" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate">{user?.name}</p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{user?.email}</p>
                          <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 capitalize">
                            {currentRole.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Menu items */}
                    <div className="p-1.5">
                      <Link
                        to="/dashboard/profile"
                        className="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-navy-700/50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                        role="menuitem"
                      >
                        <UserCircleIcon className="h-[18px] w-[18px] flex-shrink-0 text-neutral-400" />
                        <span>My Profile</span>
                      </Link>
                      <Link
                        to="/dashboard/profile?open=change-password"
                        className="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-navy-700/50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                        role="menuitem"
                      >
                        <KeyIcon className="h-[18px] w-[18px] flex-shrink-0 text-neutral-400" />
                        <span>Change Password</span>
                      </Link>
                      <Link
                        to="/dashboard/settings"
                        className="flex items-center gap-3 px-3 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-navy-700/50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                        role="menuitem"
                      >
                        <Cog6ToothIcon className="h-[18px] w-[18px] flex-shrink-0 text-neutral-400" />
                        <span>Settings</span>
                      </Link>
                    </div>

                    {/* Sign out */}
                    <div className="p-1.5 border-t border-neutral-100 dark:border-navy-700/50">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-danger-600 dark:text-danger-400 rounded-lg hover:bg-danger-50 dark:hover:bg-danger-900/10 transition-colors"
                        role="menuitem"
                      >
                        <ArrowRightOnRectangleIcon className="h-[18px] w-[18px] flex-shrink-0" />
                        <span>Sign out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content - flex child fills available space to avoid scroll */}
        <main id="main-content" className="flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-6" role="main">
          <Outlet />
        </main>
      </div>

      {/* Toast Container */}
      <ToastContainer 
        toasts={toasts} 
        onRemove={removeToast} 
      />

      {/* Notification Panel */}
      <NotificationPanel
        notifications={notifications}
        onMarkAsRead={markAsRead}
        onMarkAllAsRead={markAllAsRead}
        onDelete={deleteNotification}
        onClearAll={clearAllNotifications}
        isOpen={notificationPanelOpen}
        onClose={closeNotificationPanel}
      />

      {/* Role Switcher (Dev Only) */}
      <RoleSwitcher />
    </div>
  )
}

export const DashboardLayout: React.FC = () => {
  const { currentUser: user, isAuthenticated } = useAuthContext()
  return (
    <CompanyProvider user={user} isAuthenticated={!!isAuthenticated}>
      <DashboardLayoutInner />
    </CompanyProvider>
  )
}
